// src/features/classes/ClassesPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import ClassCard from "./ClassCard";
import { fetchClasses } from "../../data/classes";
import { Helmet } from "react-helmet-async";

const SECTION_ORDER = ["one-on-one", "group", "video"];
const SECTION_TITLES = {
  "one-on-one": "One-on-One Online Classes",
  group: "Group Online Classes",
  video: "Video Lessons",
};

const ClassesPage = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);

  // Mobile filter visibility
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  useEffect(() => {
    const loadClasses = async () => {
      try {
        const data = await fetchClasses();
        setClasses(data);
      } catch (error) {
        console.error("Failed to load classes:", error);
      } finally {
        setLoading(false);
      }
    };
    loadClasses();
  }, []);

  // Compute available filter options from the original classes
  const filterOptions = useMemo(() => {
    const types = new Set();
    const subjects = new Set();
    const grades = new Set();

    classes.forEach((item) => {
      types.add(item.lessonType || "video");
      subjects.add(item.subject || "Other");
      grades.add(item.grade);
    });

    return {
      types: Array.from(types).sort(),
      subjects: Array.from(subjects).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" })
      ),
      grades: Array.from(grades).sort((a, b) => a - b),
    };
  }, [classes]);

  // Filter classes based on selections
  const filteredClasses = useMemo(() => {
    return classes.filter((item) => {
      const typeMatch =
        selectedTypes.length === 0 || selectedTypes.includes(item.lessonType || "video");
      const subjectMatch =
        selectedSubjects.length === 0 ||
        selectedSubjects.includes(item.subject || "Other");
      const gradeMatch =
        selectedGrades.length === 0 || selectedGrades.includes(item.grade);
      return typeMatch && subjectMatch && gradeMatch;
    });
  }, [classes, selectedTypes, selectedSubjects, selectedGrades]);

  // Group filtered classes by lessonType then by subject
  const groupedByType = useMemo(() => {
    return filteredClasses.reduce((acc, item) => {
      const type = item.lessonType || "video";
      if (!acc[type]) acc[type] = [];
      acc[type].push(item);
      return acc;
    }, {});
  }, [filteredClasses]);

  // Handlers for checkbox changes
  const handleTypeChange = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleSubjectChange = (subject) => {
    setSelectedSubjects((prev) =>
      prev.includes(subject) ? prev.filter((s) => s !== subject) : [...prev, subject]
    );
  };

  const handleGradeChange = (grade) => {
    setSelectedGrades((prev) =>
      prev.includes(grade) ? prev.filter((g) => g !== grade) : [...prev, grade]
    );
  };

  // Helper to render a filter section
  const FilterSection = ({ title, options, selected, onChange }) => (
    <div className="mb-4">
      <h3 className="font-semibold text-gray-700 mb-2">{title}</h3>
      <div className="flex flex-wrap gap-3">
        {options.map((option) => (
          <label key={option} className="inline-flex items-center">
            <input
              type="checkbox"
              className="form-checkbox h-4 w-4 text-blue-600"
              checked={selected.includes(option)}
              onChange={() => onChange(option)}
            />
            <span className="ml-2 text-sm text-gray-700">{option}</span>
          </label>
        ))}
      </div>
    </div>
  );

  //const handleEnroll = (classItem) => {
  //alert(`You enrolled in ${classItem.title}!`);
  // Integrate with cart/order system here
  //};

  const handleEnroll = (classItem) => {
    if (classItem.enrollUrl) {
      // Open in new tab with security best practices
      console.log("ErollUrl", classItem.enrollUrl)
      window.open(classItem.enrollUrl, '_blank', 'noopener,noreferrer');
    } else {
      alert('Enrollment link not available for this class.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (classes.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500">No classes available at the moment.</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Online Classes, SPK Academy & Publishing</title>
        <meta
          name="description"
          content="Enroll in online classes for Mathematics and Physical Sciences. Choose from one-on-one tutoring, group classes, or self-paced video lessons with assessment books included."
        />
        <meta property="og:title" content="Online Classes, SPK Academy & Publishing" />
        <meta
          property="og:description"
          content="SPK Academy offers flexible online learning options with expert teachers, assessment books, and personalized feedback."
        />
        <meta property="og:url" content="https://spkrobertacademy.com/classes" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-2 sm:px-2 lg:px-4 py-12">
        <h1 className="text-4xl font-extrabold text-gray-900 mb-8 text-center">
          Online Classes
        </h1>

        {/* Mobile filter toggle button */}
        <div className="md:hidden mb-4">
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="w-full bg-white border border-gray-300 rounded-lg px-2 py-2 text-left text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-expanded={showMobileFilters}
          >
            <span className="flex justify-between items-center">
              <span>Filters</span>
              <svg
                className={`w-5 h-5 transform transition-transform ${showMobileFilters ? "rotate-180" : ""
                  }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </span>
          </button>
        </div>

        {/* Filters panel */}
        <div
          className={`${showMobileFilters ? "block" : "hidden"
            } md:block bg-white p-6 rounded-lg shadow-md mb-10`}
        >
          <h2 className="text-xl font-bold text-gray-800 mb-4">Filter Classes</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <FilterSection
              title="Lesson Type"
              options={filterOptions.types}
              selected={selectedTypes}
              onChange={handleTypeChange}
            />
            <FilterSection
              title="Subject"
              options={filterOptions.subjects}
              selected={selectedSubjects}
              onChange={handleSubjectChange}
            />
            <FilterSection
              title="Grade"
              options={filterOptions.grades}
              selected={selectedGrades}
              onChange={handleGradeChange}
            />
          </div>
          <button
            onClick={() => {
              setSelectedTypes([]);
              setSelectedSubjects([]);
              setSelectedGrades([]);
            }}
            className="mt-4 text-sm text-blue-600 hover:text-blue-800"
          >
            Clear all filters
          </button>
        </div>

        {/* Sections */}
        {SECTION_ORDER.map((type) => {
          const typeItems = groupedByType[type];
          if (!typeItems || typeItems.length === 0) return null;

          // Group by subject within this section
          const groupedBySubject = typeItems.reduce((acc, item) => {
            const subject = item.subject || "Other";
            if (!acc[subject]) acc[subject] = [];
            acc[subject].push(item);
            return acc;
          }, {});

          // Sort subjects alphabetically
          const sortedSubjects = Object.keys(groupedBySubject).sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: "base" })
          );

          return (
            <section key={type} className="mb-16">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 border-b-2 border-gray-200 pb-2">
                {SECTION_TITLES[type]}
              </h2>

              {sortedSubjects.map((subject) => (
                <div key={subject} className="mb-10">
                  <h3 className="text-2xl font-semibold text-gray-700 mb-4">
                    {subject}
                  </h3>
                  <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {groupedBySubject[subject].map((classItem) => (
                      <ClassCard
                        key={classItem.id}
                        classItem={classItem}
                        onEnroll={handleEnroll}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </section>
          );
        })}

        {filteredClasses.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No classes match your filters.</p>
          </div>
        )}
      </div>
    </>
  );
};

export default ClassesPage;